import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Tabs, Tab, Table, Alert, Spinner, Badge } from 'react-bootstrap';
import { toast } from 'react-toastify';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import './ReporteSocio.css';
import { getNombreMes, encontrarPrestamoActivo, calcularDeudaPendiente } from '../utils/amortizacion';

const ReporteSocio = () => {
  const { user, isSocio } = useAuth();
  const [loading, setLoading] = useState(true);
  const [registrosMensuales, setRegistrosMensuales] = useState([]);
  const [prestamos, setPrestamos] = useState([]);
  const [prestamosFinalizados, setPrestamosFinalizados] = useState([]);
  const [expandedLoan, setExpandedLoan] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [regResponse, prestResponse, finResponse] = await Promise.all([
        axios.get('/api/registros-mensuales/mis-registros'),
        axios.get('/api/solicitudes-prestamo/mis-solicitudes'),
        axios.get('/api/app/prestamos/finalizados').catch(() => ({ data: { data: [] } }))
      ]);
      
      setRegistrosMensuales(regResponse.data.data || []);
      setPrestamos(prestResponse.data.data || []);
      setPrestamosFinalizados(finResponse.data.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error al cargar los datos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isSocio()) {
      fetchData();
    } else {
      setLoading(false);
    }
  }, [isSocio, fetchData]);

  const formatCurrency = (num) => {
    return parseFloat(num || 0).toLocaleString('es-ES', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  // Calcular acumulados
  const calcularAcumulados = () => {
    const aportacionesTotal = registrosMensuales.reduce((acc, reg) => acc + (reg.aportes || 0), 0);
    const paseoTotal = registrosMensuales.reduce((acc, reg) => acc + (reg.paseo || 0), 0);
    const capitalPagadoTotal = registrosMensuales.reduce((acc, reg) => acc + (reg.capitalPrestamo || 0), 0);
    const interesPagadoTotal = registrosMensuales.reduce((acc, reg) => acc + (reg.interesPrestamo || 0), 0);
    
    // Deuda del préstamo activo con la misma lógica que el cronograma (antes
    // restaba los pagos de TODOS los préstamos del socio, incluidos los viejos).
    const prestamoActivo = encontrarPrestamoActivo(prestamos);
    const deuda = calcularDeudaPendiente(prestamoActivo, registrosMensuales);
    const deudaSinInteres = deuda.sinInteres;
    const deudaPendiente = deuda.conInteres;
    
    return {
      aportacionesTotal,
      paseoTotal,
      capitalPagadoTotal,
      interesPagadoTotal,
      deudaSinInteres,
      deudaPendiente
    };
  };

  if (!isSocio()) {
    return (
      <Container className="mt-4">
        <Alert variant="warning">
          <i className="fas fa-exclamation-triangle me-2"></i>
          Este reporte es solo para socios.
        </Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container className="mt-4 text-center">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Cargando...</span>
        </Spinner>
      </Container>
    );
  }

  const acumulados = calcularAcumulados();

  return (
    <Container fluid className="mt-4 mb-4">
      <Row className="mb-4">
        <Col>
          <h3 className="mb-3">
            <i className="fas fa-chart-bar me-2"></i>
            Reportes Financieros
          </h3>
        </Col>
      </Row>

      <Tabs defaultActiveKey="mensual" className="mb-4">
        {/* TAB: REPORTE MENSUAL */}
        <Tab eventKey="mensual" title="📅 Reporte Mensual">
          {/* Cards Summary */}
          <Row className="mb-4">
            <Col md={3}>
              <Card className="text-center border-success">
                <Card.Body>
                  <h6 className="text-muted mb-2">Aportaciones Totales</h6>
                  <h4 className="text-success mb-0">
                    <i className="fas fa-coins me-2"></i>
                    {formatCurrency(registrosMensuales.reduce((sum, r) => sum + (r.aportes || 0), 0))}
                  </h4>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="text-center border-info">
                <Card.Body>
                  <h6 className="text-muted mb-2">Paseos Totales</h6>
                  <h4 className="text-info mb-0">
                    <i className="fas fa-users me-2"></i>
                    {formatCurrency(registrosMensuales.reduce((sum, r) => sum + (r.paseo || 0), 0))}
                  </h4>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="text-center border-warning">
                <Card.Body>
                  <h6 className="text-muted mb-2">Capital Préstamo Pagado</h6>
                  <h4 className="text-warning mb-0">
                    <i className="fas fa-money-bill me-2"></i>
                    {formatCurrency(registrosMensuales.reduce((sum, r) => sum + (r.capitalPrestamo || 0), 0))}
                  </h4>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="text-center border-danger">
                <Card.Body>
                  <h6 className="text-muted mb-2">Interés Pagado</h6>
                  <h4 className="text-danger mb-0">
                    <i className="fas fa-percent me-2"></i>
                    {formatCurrency(registrosMensuales.reduce((sum, r) => sum + (r.interesPrestamo || 0), 0))}
                  </h4>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Card>
            <Card.Header className="bg-light">
              <h5 className="mb-0">Desglose Mensual de Movimientos</h5>
            </Card.Header>
            <Card.Body>
              {registrosMensuales.length === 0 ? (
                <Alert variant="info">No hay registros mensuales disponibles</Alert>
              ) : (
                <div className="table-responsive">
                  <Table striped bordered hover size="sm" className="mb-0">
                    <thead className="table-primary">
                      <tr>
                        <th>Mes</th>
                        <th>Año</th>
                        <th>Aportaciones</th>
                        <th>Paseo</th>
                        <th>Capital Préstamo</th>
                        <th>Interés Préstamo</th>
                        <th>Total Movimiento</th>
                        <th>Observaciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {registrosMensuales.sort((a, b) => {
                        if (a.año !== b.año) return a.año - b.año;
                        return a.mes - b.mes;
                      }).map((registro, idx) => {
                        const totalMov = (registro.aportes || 0) + (registro.paseo || 0) + (registro.capitalPrestamo || 0) + (registro.interesPrestamo || 0);
                        return (
                          <tr key={idx}>
                            <td><strong>{getNombreMes(registro.mes)}</strong></td>
                            <td className="text-center">{registro.año}</td>
                            <td className="text-right text-success">{formatCurrency(registro.aportes || 0)}</td>
                            <td className="text-right text-info">{formatCurrency(registro.paseo || 0)}</td>
                            <td className="text-right text-warning">{formatCurrency(registro.capitalPrestamo || 0)}</td>
                            <td className="text-right text-danger">{formatCurrency(registro.interesPrestamo || 0)}</td>
                            <td className="text-right"><strong>{formatCurrency(totalMov)}</strong></td>
                            <td className="text-muted small">{registro.observaciones || '-'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
          </Card>
        </Tab>

        {/* TAB: REPORTE ACUMULADO */}
        <Tab eventKey="acumulado" title="📊 Reporte Acumulado">
          <Row className="mb-4">
            <Col md={6}>
              <Card className="h-100 border-success">
                <Card.Header className="bg-success text-white">
                  <h6 className="mb-0">
                    <i className="fas fa-arrow-up me-2"></i>
                    Ingresos Acumulados
                  </h6>
                </Card.Header>
                <Card.Body>
                  <div className="mb-3">
                    <small className="text-muted">Aportaciones Totales</small>
                    <h4 className="text-success mb-0">{formatCurrency(acumulados.aportacionesTotal)}</h4>
                  </div>
                  <div>
                    <small className="text-muted">Paseos Totales</small>
                    <h4 className="text-info mb-0">{formatCurrency(acumulados.paseoTotal)}</h4>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col md={6}>
              <Card className="h-100 border-danger">
                <Card.Header className="bg-danger text-white">
                  <h6 className="mb-0">
                    <i className="fas fa-arrow-down me-2"></i>
                    Pagos Acumulados
                  </h6>
                </Card.Header>
                <Card.Body>
                  <div className="mb-3">
                    <small className="text-muted">Capital Pagado</small>
                    <h4 className="text-warning mb-0">{formatCurrency(acumulados.capitalPagadoTotal)}</h4>
                  </div>
                  <div>
                    <small className="text-muted">Interés Pagado</small>
                    <h4 className="text-danger mb-0">{formatCurrency(acumulados.interesPagadoTotal)}</h4>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Card className="border-warning">
            <Card.Header className="bg-warning text-dark">
              <h6 className="mb-0">
                <i className="fas fa-balance-scale me-2"></i>
                Saldo Actual
              </h6>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={4} className="text-center">
                  <div className="p-3 border rounded">
                    <small className="text-muted">Pagos Realizados</small>
                    <h3 className="text-info mb-0">
                      {formatCurrency(acumulados.capitalPagadoTotal + acumulados.interesPagadoTotal)}
                    </h3>
                  </div>
                </Col>
                <Col md={4} className="text-center">
                  <div className="p-3 border rounded">
                    <small className="text-muted">Deuda Sin Interés</small>
                    <h3 className={acumulados.deudaSinInteres > 0 ? 'text-warning' : 'text-success'}>
                      {formatCurrency(acumulados.deudaSinInteres)}
                    </h3>
                  </div>
                </Col>
                <Col md={4} className="text-center">
                  <div className="p-3 border rounded">
                    <small className="text-muted">Deuda Con Interés</small>
                    <h3 className={acumulados.deudaPendiente > 0 ? 'text-danger' : 'text-success'}>
                      {formatCurrency(acumulados.deudaPendiente)}
                    </h3>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Tabla de resumen acumulado */}
          <Card className="mt-4">
            <Card.Header className="bg-light">
              <h5 className="mb-0">Resumen Acumulado Detallado</h5>
            </Card.Header>
            <Card.Body>
              <div className="table-responsive">
                <Table borderless className="mb-0">
                  <tbody>
                    <tr>
                      <td className="fw-bold">Total Aportaciones:</td>
                      <td className="text-end">{formatCurrency(acumulados.aportacionesTotal)}</td>
                    </tr>
                    <tr>
                      <td className="fw-bold">Total Paseos:</td>
                      <td className="text-end">{formatCurrency(acumulados.paseoTotal)}</td>
                    </tr>
                    <tr className="border-top">
                      <td className="fw-bold">Capital Pagado del Préstamo:</td>
                      <td className="text-end">{formatCurrency(acumulados.capitalPagadoTotal)}</td>
                    </tr>
                    <tr>
                      <td className="fw-bold">Interés Pagado del Préstamo:</td>
                      <td className="text-end">{formatCurrency(acumulados.interesPagadoTotal)}</td>
                    </tr>
                    <tr className="border-top">
                      <td className="fw-bold">Total Pagado:</td>
                      <td className="text-end text-info fw-bold">
                        {formatCurrency(acumulados.capitalPagadoTotal + acumulados.interesPagadoTotal)}
                      </td>
                    </tr>
                    <tr className="border-top border-danger">
                      <td className="fw-bold">Deuda Pendiente:</td>
                      <td className={`text-end fw-bold ${acumulados.deudaPendiente > 0 ? 'text-danger' : 'text-success'}`}>
                        {formatCurrency(acumulados.deudaPendiente)}
                      </td>
                    </tr>
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </Tab>

        {/* TAB: PRÉSTAMOS FINALIZADOS */}
        <Tab eventKey="finalizados" title="✅ Préstamos Finalizados">
          {prestamosFinalizados.length === 0 ? (
            <Alert variant="info" className="mt-4">
              <i className="fas fa-info-circle me-2"></i>
              No tienes préstamos finalizados aún.
            </Alert>
          ) : (
            <Row className="mt-4">
              {prestamosFinalizados.map((prestamo) => (
                <Col lg={6} className="mb-4" key={prestamo._id}>
                  <Card className="h-100 shadow-sm border-success">
                    <Card.Header className="bg-success text-white d-flex justify-content-between align-items-center">
                      <span>
                        <i className="fas fa-check-circle me-2"></i>
                        Préstamo {prestamo.numeroPrestamo || prestamo._id.slice(-6)}
                      </span>
                      <Badge bg="light" text="dark">Finalizado</Badge>
                    </Card.Header>
                    <Card.Body>
                      <Row className="mb-3">
                        <Col md={6}>
                          <div className="mb-3">
                            <small className="text-muted">Monto Original</small>
                            <h6 className="mb-0">{formatCurrency(prestamo.monto)}</h6>
                          </div>
                          <div className="mb-3">
                            <small className="text-muted">Plazo</small>
                            <h6 className="mb-0">{(prestamo.plazo || 0) + (prestamo.mesesExtendidos || 0)} meses</h6>
                          </div>
                        </Col>
                        <Col md={6}>
                          <div className="mb-3">
                            <small className="text-muted">Capital Pagado</small>
                            <h6 className="mb-0 text-success">{formatCurrency(prestamo.capitalPagado)}</h6>
                          </div>
                          <div className="mb-3">
                            <small className="text-muted">Interés Pagado</small>
                            <h6 className="mb-0 text-danger">{formatCurrency(prestamo.interesPagado)}</h6>
                          </div>
                        </Col>
                      </Row>

                      <hr />

                      <div className="mb-3">
                        <small className="text-muted">Total Pagado</small>
                        <h5 className="mb-0 text-success">
                          <i className="fas fa-check me-2"></i>
                          {formatCurrency(prestamo.totalPagado)}
                        </h5>
                      </div>

                      <button
                        className="btn btn-sm btn-outline-primary w-100"
                        onClick={() => setExpandedLoan(expandedLoan === prestamo._id ? null : prestamo._id)}
                      >
                        <i className={`fas fa-${expandedLoan === prestamo._id ? 'chevron-up' : 'chevron-down'} me-2`}></i>
                        {expandedLoan === prestamo._id ? 'Ocultar' : 'Ver'} Tabla de Amortización
                      </button>

                      {expandedLoan === prestamo._id && prestamo.registros && (
                        <div className="mt-4 pt-3 border-top">
                          <h6 className="mb-3">Historial de Pagos</h6>
                          <div className="table-responsive">
                            <Table striped bordered hover size="sm" className="mb-0">
                              <thead className="table-light">
                                <tr>
                                  <th>Cuota</th>
                                  <th>Capital</th>
                                  <th>Interés</th>
                                  <th>Estado</th>
                                  <th>Fecha</th>
                                </tr>
                              </thead>
                              <tbody>
                                {prestamo.registros.map((reg, idx) => {
                                  const estadoPago = 
                                    (reg.capitalPrestamo || 0) > 0 ? 'Pagada' :
                                    (reg.interesPrestamo || 0) > 0 ? 'Pagó Interés' : 'Pendiente';
                                  
                                  return (
                                    <tr key={idx}>
                                      <td className="text-center fw-bold">{idx + 1}</td>
                                      <td className="text-end">{formatCurrency(reg.capitalPrestamo)}</td>
                                      <td className="text-end">{formatCurrency(reg.interesPrestamo)}</td>
                                      <td>
                                        <Badge bg={
                                          estadoPago === 'Pagada' ? 'success' :
                                          estadoPago === 'Pagó Interés' ? 'info' : 'secondary'
                                        }>
                                          {estadoPago}
                                        </Badge>
                                      </td>
                                      <td className="text-center">{reg.mes}/{reg.año}</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </Table>
                          </div>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          )}
        </Tab>
      </Tabs>
    </Container>
  );
};

export default ReporteSocio;

